import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/molecules/Tabs';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Spinner } from '@/components/atoms/Spinner';
import { Badge } from '@/components/atoms/Badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/molecules/Dialog';
import { useAsyncData, useAsyncMutation } from '@/hooks/useAsyncData';
import {
  backofficeRewardsAPI,
  type BackofficeReward,
} from '@/services/api/backofficeRewards.api';
import styles from './RewardsListPage.module.scss';

type TabKey = 'active' | 'inactive' | 'all';
const TABS: TabKey[] = ['active', 'inactive', 'all'];
const TAB_LABEL: Record<TabKey, string> = { active: 'Activas', inactive: 'Inactivas', all: 'Todas' };

interface DialogState {
  mode: 'create' | 'edit';
  reward?: BackofficeReward;
}

export function RewardsListPage() {
  const [tab, setTab] = useState<TabKey>('active');
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const list = useAsyncData({
    fetchFn: () => backofficeRewardsAPI.list(tab),
    dependencies: [tab],
  });

  const toggleActive = useAsyncMutation({
    mutationFn: (r: BackofficeReward) => backofficeRewardsAPI.update(r.id, { active: !r.active }),
    onSuccess: () => void list.refetch(),
  });

  const remove = useAsyncMutation({
    mutationFn: (id: string) => backofficeRewardsAPI.remove(id),
    onSuccess: () => void list.refetch(),
  });

  const supportedTypes = list.data?.supportedTypes ?? ['streak_protector'];
  const items = list.data?.items ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.page__header}>
        <div>
          <h1 className={styles.page__title}>Recompensas</h1>
          <p className={styles.page__subtitle}>
            Catálogo global de recompensas canjeables. Solo recompensas con efecto programado
            (tipo). Canjear no afecta al ranking del alumno.
          </p>
        </div>
        <Button variant="primary" onClick={() => setDialog({ mode: 'create' })}>
          Nueva recompensa
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          {TABS.map((k) => (
            <TabsTrigger key={k} value={k}>
              {TAB_LABEL[k]}
            </TabsTrigger>
          ))}
        </TabsList>

        {TABS.map((k) => (
          <TabsContent key={k} value={k}>
            <Card>
              <CardHeader>
                <CardTitle>{TAB_LABEL[k]}</CardTitle>
              </CardHeader>
              <CardContent>
                {list.isLoading ? (
                  <div className={styles.page__loading}>
                    <Spinner size="lg" aria-label="Cargando" />
                  </div>
                ) : items.length === 0 ? (
                  <div className={styles.page__empty}>
                    <p>No hay recompensas en este filtro.</p>
                  </div>
                ) : (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Imagen</th>
                        <th>Nombre (EN)</th>
                        <th>Tipo</th>
                        <th>Coste</th>
                        <th>Estado</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((r) => (
                        <tr key={r.id}>
                          <td>
                            {r.imageUrl ? (
                              <img src={r.imageUrl} alt="" className={styles.table__thumb} />
                            ) : (
                              <span className={styles.table__noImage}>—</span>
                            )}
                          </td>
                          <td>{r.title ?? <span className={styles.table__warning}>Sin nombre</span>}</td>
                          <td>
                            <code>{r.type}</code>
                          </td>
                          <td>{r.cost} XP</td>
                          <td>
                            <div className={styles.table__badges}>
                              <Badge variant={r.active ? 'success' : 'default'}>
                                {r.active ? 'Activa' : 'Inactiva'}
                              </Badge>
                              {r.comingSoon && <Badge variant="secondary">Próximamente</Badge>}
                            </div>
                          </td>
                          <td>
                            <div className={styles.table__actions}>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDialog({ mode: 'edit', reward: r })}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                isLoading={toggleActive.isLoading}
                                onClick={() => void toggleActive.mutate(r)}
                              >
                                {r.active ? 'Desactivar' : 'Activar'}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                isLoading={remove.isLoading}
                                onClick={() => void remove.mutate(r.id)}
                              >
                                Borrar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {dialog && (
        <RewardEditDialog
          state={dialog}
          supportedTypes={supportedTypes}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            void list.refetch();
          }}
        />
      )}
    </div>
  );
}

interface RewardEditDialogProps {
  state: DialogState;
  supportedTypes: string[];
  onClose: () => void;
  onSaved: () => void;
}

function RewardEditDialog({ state, supportedTypes, onClose, onSaved }: RewardEditDialogProps) {
  const r = state.reward;
  const [type, setType] = useState(r?.type ?? supportedTypes[0] ?? 'streak_protector');
  const [title, setTitle] = useState(r?.title ?? '');
  const [titleTranslation, setTitleTranslation] = useState(r?.titleTranslation ?? '');
  const [description, setDescription] = useState(r?.description ?? '');
  const [descriptionTranslation, setDescriptionTranslation] = useState(r?.descriptionTranslation ?? '');
  const [cost, setCost] = useState(String(r?.cost ?? ''));
  const [active, setActive] = useState(r?.active ?? true);
  const [comingSoon, setComingSoon] = useState(r?.comingSoon ?? false);
  const [imageUrl, setImageUrl] = useState<string | null>(r?.imageUrl ?? null);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setType(r?.type ?? supportedTypes[0] ?? 'streak_protector');
    setTitle(r?.title ?? '');
    setTitleTranslation(r?.titleTranslation ?? '');
    setDescription(r?.description ?? '');
    setDescriptionTranslation(r?.descriptionTranslation ?? '');
    setCost(String(r?.cost ?? ''));
    setActive(r?.active ?? true);
    setComingSoon(r?.comingSoon ?? false);
    setImageUrl(r?.imageUrl ?? null);
    setErrorMessage(null);
  }, [r, supportedTypes]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setErrorMessage(null);
    try {
      const { url } = await backofficeRewardsAPI.uploadImage(file);
      setImageUrl(url);
    } catch {
      setErrorMessage('No se pudo subir la imagen.');
    } finally {
      setUploading(false);
    }
  };

  const save = useAsyncMutation({
    mutationFn: async () => {
      const payload = {
        type,
        title: title.trim(),
        titleTranslation: titleTranslation.trim() || null,
        description: description.trim() || null,
        descriptionTranslation: descriptionTranslation.trim() || null,
        imageUrl,
        cost: Number(cost),
        active,
        comingSoon,
      };
      if (state.mode === 'create') return backofficeRewardsAPI.create(payload);
      if (!r) throw new Error('Missing reward');
      return backofficeRewardsAPI.update(r.id, payload);
    },
    onSuccess: () => onSaved(),
    onError: (e) => setErrorMessage(e.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!title.trim()) {
      setErrorMessage('El nombre en inglés es obligatorio.');
      return;
    }
    const costNum = Number(cost);
    if (!Number.isFinite(costNum) || costNum < 0) {
      setErrorMessage('El coste debe ser un número válido.');
      return;
    }
    void save.mutate(undefined as never);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {state.mode === 'create' ? 'Crear recompensa' : 'Editar recompensa'}
          </DialogTitle>
        </DialogHeader>
        <form className={styles.dialog__form} onSubmit={handleSubmit}>
          <div className={styles.dialog__row}>
            <label htmlFor="rw-type">Tipo (efecto)</label>
            <select id="rw-type" value={type} onChange={(e) => setType(e.target.value)} className={styles.dialog__select}>
              {supportedTypes.map((tp) => (
                <option key={tp} value={tp}>
                  {tp}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.dialog__row}>
            <label htmlFor="rw-title">Nombre (EN)</label>
            <Input id="rw-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className={styles.dialog__row}>
            <label htmlFor="rw-title-es">Nombre (ES)</label>
            <Input id="rw-title-es" value={titleTranslation} onChange={(e) => setTitleTranslation(e.target.value)} />
          </div>
          <div className={styles.dialog__row}>
            <label htmlFor="rw-desc">Descripción (EN)</label>
            <Input id="rw-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className={styles.dialog__row}>
            <label htmlFor="rw-desc-es">Descripción (ES)</label>
            <Input id="rw-desc-es" value={descriptionTranslation} onChange={(e) => setDescriptionTranslation(e.target.value)} />
          </div>
          <div className={styles.dialog__row}>
            <label htmlFor="rw-cost">Coste (XP)</label>
            <Input id="rw-cost" type="number" value={cost} onChange={(e) => setCost(e.target.value)} required />
          </div>
          <div className={styles.dialog__row}>
            <label htmlFor="rw-image">Imagen</label>
            {imageUrl && <img src={imageUrl} alt="" className={styles.dialog__preview} />}
            <input
              id="rw-image"
              type="file"
              accept="image/*"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleUpload(file);
              }}
            />
            {uploading && <span className={styles.dialog__hint}>Subiendo…</span>}
          </div>
          <label className={styles.dialog__check}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Activa (visible para los alumnos)
          </label>
          <label className={styles.dialog__check}>
            <input type="checkbox" checked={comingSoon} onChange={(e) => setComingSoon(e.target.checked)} />
            Próximamente (se muestra con la etiqueta, pero no se puede canjear)
          </label>
          {errorMessage && <p className={styles.dialog__error}>{errorMessage}</p>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={save.isLoading} disabled={uploading}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default RewardsListPage;
